import {
  RetailerFormReducerState,
  UpdateRetailerSingleInputField,
  UpdateRetailerListField,
  UpdateRetailerMultiInputField,
} from 'reducers/CreateAndEditRetailerReducer/CreateAndEditRetailerReducer'
import {
  UpdateContentBlockInputField,
  UpdateContentBlockListField,
  ContentBlockFormReducerState,
  UpdateContentBlockSelectField,
  UpdateContentBlockMultiSelectField,
} from 'reducers/CreateAndEditRetailerReducer/RetailerContentBlockReducer'
import {
  BaseValidationField,
  BaseErrorType,
  ErrorType,
  FormReducerListField,
  FormReducerMultiSelectField,
  FormReducerMultiTextField,
  FormReducerSelectField,
  FormReducerSingleTextField,
  addInputItemToArray,
  addListItemString,
  addSelectValueString,
  removeListItem,
  removeSelectValueString,
  replaceListItem,
} from './utils'
import { BrandLanguageFormReducerState } from 'reducers/CreateAndEditRetailerReducer/RetailerBrandLanguageReducer'
import { ProjectFormReducerState } from 'reducers/CreateAndEditNewProjectReducer/CreateAndEditNewProjectReducer'
import {
  AddRegionToProjectFormReducerState,
  AddRegionToProjectMultiSelectField,
} from 'reducers/AddAndEditProjectRegionReducer/AddAndEditProjectRegionReducer'
import {
  BrandFormReducerState,
  UpdateBrandListField,
  UpdateBrandMultiInputField,
  UpdateBrandSingleInputField,
} from 'reducers/CreateAndEditBrandReducer/CreateAndEditBrandReducer'
import { EditProductFormReducerState } from 'reducers/EditProductReducer/EditProductReducer'
import { EditVariantFormReducerState } from 'reducers/EditVariantReducer/EditVariantReducer'
import {
  RegionFormReducerState,
  UpdateAutoCompleteField,
} from 'reducers/CreateAndEditRegionReducer/CreateAndEditRegionReducer'
import { AutocompleteDefaultOption } from '@wppopen/components-library'

export const errorAndWarningAttributes: BaseErrorType = { error: undefined, warning: undefined }
export const emptySingleInputField: FormReducerSingleTextField = {
  value: '',
  type: 'text',
  ...errorAndWarningAttributes,
}
export const emptyMultiInputField: FormReducerMultiTextField = {
  value: [],
  type: 'multiText',
  ...errorAndWarningAttributes,
}
export const emptyListField: FormReducerListField = { value: [], type: 'list', ...errorAndWarningAttributes }
export const emptySelectField: FormReducerSelectField = { value: '', type: 'select', ...errorAndWarningAttributes }
export const emptyMultiSelectField: FormReducerMultiSelectField = {
  value: [],
  type: 'multiSelect',
  ...errorAndWarningAttributes,
}

// Get the keys of the fields that have a value and an error field
type ValidationField<T extends keyof StateToTypeMap> = BaseValidationField<StateToTypeMap[T]>

export type UpdateActionType = 'add' | 'replace' | 'remove' | 'remove_all'

// Define all update actions
export type UpdateField =
  | FormReducerSingleTextField
  | FormReducerMultiTextField
  | FormReducerListField
  | FormReducerSelectField
  | FormReducerMultiSelectField

// TODO: automate these types based on extracted values from Mapped fields below
// Define the keys of the fields that match the correct element types
type RetailerOrBrandSingleInput = UpdateRetailerSingleInputField | UpdateBrandSingleInputField
export type UpdateSingleInputField = RetailerOrBrandSingleInput | UpdateContentBlockInputField
export type UpdateMultiInputField = UpdateRetailerMultiInputField | UpdateBrandMultiInputField
export type UpdateListField = UpdateRetailerListField | UpdateContentBlockListField | 'guidelines'
export type UpdateSelectField = UpdateContentBlockSelectField | UpdateBrandListField
export type UpdateMultiSelectField = UpdateContentBlockMultiSelectField
type RegionAndDropdownSelectField = UpdateSelectField | UpdateMultiSelectField | AddRegionToProjectMultiSelectField
export type SelectField = RegionAndDropdownSelectField | 'locale' | 'flag'

type FieldMap<T extends keyof StateToTypeMap, U> = {
  [K in keyof StateToTypeMap[T]]: StateToTypeMap[T][K] extends U ? K : never
}[keyof StateToTypeMap[T]]

export interface StateToTypeMap {
  RetailerFormReducerState: RetailerFormReducerState
  ContentBlockFormReducerState: ContentBlockFormReducerState
  BrandLanguageFormReducerState: BrandLanguageFormReducerState
  ProjectFormReducerState: ProjectFormReducerState
  AddRegionToProjectFormReducerState: AddRegionToProjectFormReducerState
  BrandFormReducerState: BrandFormReducerState
  EditProductFormReducerState: EditProductFormReducerState
  EditVariantFormReducerState: EditVariantFormReducerState
  RegionFormReducerState: RegionFormReducerState
}

// Create new types that map the keys of `StateToTypeMap[T]` to their corresponding field types
export type StateToMultiInputFieldMap<T extends keyof StateToTypeMap> = FieldMap<T, FormReducerMultiTextField>
export type StateToSingleInputFieldMap<T extends keyof StateToTypeMap> = FieldMap<T, FormReducerSingleTextField>
export type StateToListFieldMap<T extends keyof StateToTypeMap> = FieldMap<T, FormReducerListField>
export type StateToSingleSelectFieldMap<T extends keyof StateToTypeMap> = FieldMap<T, FormReducerSelectField>
export type StateToMultiSelectFieldMap<T extends keyof StateToTypeMap> = FieldMap<T, FormReducerMultiSelectField>

// Define the actions for the form
export type FormReducerAction =
  | { type: typeof UPDATE_SINGLE_INPUT; payload: { field: UpdateSingleInputField; value: string } }
  | {
      type: typeof UPDATE_MULTI_INPUT
      payload: { field: UpdateMultiInputField; actionType: UpdateActionType; idx: number | null; value: string }
    }
  | {
      type: typeof UPDATE_AUTOCOMPLETE
      payload: {
        field: UpdateMultiSelectField | UpdateAutoCompleteField
        value: string[] | AutocompleteDefaultOption[]
      }
    }
  | {
      type: typeof UPDATE_LIST
      payload: { field: UpdateListField; value: string; idx: number | null; actionType: UpdateActionType }
    }
  | { type: typeof UPDATE_SELECT; payload: { field: UpdateSelectField; value: string } }
  | {
      type: typeof UPDATE_MULTI_SELECT
      payload: { field: UpdateMultiSelectField; value: string; actionType: UpdateActionType }
    }
  | {
      type: typeof SET_SELECT
      payload: { field: SelectField; value: string | string[] }
    }
  | { type: typeof HANDLE_ERROR; payload: { field: ValidationField<any>; value: ErrorType } }
  | { type: typeof HANDLE_WARNING; payload: { field: ValidationField<any>; value: ErrorType } }
  | { type: typeof RESET_FORM; payload: StateToTypeMap[keyof StateToTypeMap] }

// action types for reducer
const UPDATE_SINGLE_INPUT = 'UPDATE_SINGLE_INPUT'
const UPDATE_MULTI_INPUT = 'UPDATE_MULTI_INPUT'
const UPDATE_LIST = 'UPDATE_LIST'
const UPDATE_SELECT = 'UPDATE_SELECT'
const UPDATE_MULTI_SELECT = 'UPDATE_MULTI_SELECT'
const SET_SELECT = 'SET_SELECT'
const UPDATE_AUTOCOMPLETE = 'UPDATE_AUTOCOMPLETE'
const HANDLE_ERROR = 'HANDLE_ERROR'
const HANDLE_WARNING = 'HANDLE_WARNING'
const RESET_FORM = 'RESET_FORM'

// Create the form reducer
function formReducer<T extends keyof StateToTypeMap>(
  state: StateToTypeMap[T],
  action: FormReducerAction,
): StateToTypeMap[T] {
  type ReturnState = StateToTypeMap[T]

  const { type, payload } = action

  switch (type) {
    case UPDATE_SINGLE_INPUT: {
      const { field, value } = payload
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], value },
      }
      return returnValue
    }
    case UPDATE_MULTI_INPUT: {
      const { field, actionType, idx, value } = payload
      let selectedValues = Array.from((state[field as keyof ReturnState] as FormReducerMultiSelectField).value)
      switch (actionType) {
        case 'add': {
          if (idx !== null) throw new Error('idx should be null for add action')
          selectedValues = addInputItemToArray(selectedValues, value)
          break
        }
        case 'remove': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          if (value !== '') throw new Error('value should be empty for remove action')
          selectedValues = removeListItem(selectedValues, idx)
          break
        }
      }
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], value: selectedValues },
      }
      return returnValue
    }
    case UPDATE_LIST: {
      const { field, value, idx, actionType } = payload
      let selectedValues = Array.from((state[field as keyof ReturnState] as FormReducerListField).value)
      switch (actionType) {
        case 'add': {
          if (idx !== null) throw new Error('idx should be null for add action')
          if (value !== '') throw new Error('value should be empty for add action')
          selectedValues = addListItemString(selectedValues)
          break
        }
        case 'remove': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          if (value !== '') throw new Error('value should be empty for remove action')
          selectedValues = removeListItem(selectedValues, idx)
          break
        }
        case 'replace': {
          if (idx === null) throw new Error('idx should not be null for remove action')
          selectedValues = replaceListItem(selectedValues, value, idx)
          break
        }
      }
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], value: selectedValues },
      }
      return returnValue
    }
    case UPDATE_SELECT: {
      const { field, value } = payload
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = { ...state, [field]: { ...state[field as keyof ReturnState], value } }
      return returnValue
    }

    case UPDATE_MULTI_SELECT: {
      const { field, value, actionType } = payload
      let selectedValues = Array.from((state[field as keyof ReturnState] as FormReducerMultiSelectField).value)
      switch (actionType) {
        case 'add': {
          selectedValues = addSelectValueString(selectedValues, value)
          break
        }
        case 'remove': {
          selectedValues = removeSelectValueString(selectedValues, value)
          break
        }
        case 'remove_all': {
          selectedValues = []
          break
        }
      }
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], value: selectedValues },
      }
      return returnValue
    }

    case UPDATE_AUTOCOMPLETE: {
      const { field, value } = payload
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], value },
      }
      return returnValue
    }

    case SET_SELECT: {
      const { field, value } = payload

      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = { ...state, [field]: { ...state[field as keyof ReturnState], value } }
      return returnValue
    }

    case HANDLE_ERROR: {
      const { field, value } = payload
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], error: value },
      }
      return returnValue
    }

    case HANDLE_WARNING: {
      const { field, value } = payload
      // declare return object outside of return statement to for type checking
      const returnValue: ReturnState = {
        ...state,
        [field]: { ...state[field as keyof ReturnState], warning: value },
      }
      return returnValue
    }

    case RESET_FORM: {
      return payload as ReturnState
    }

    default:
      return state
  }
}

// A factory function that returns the typesafe helper functions
export function formHelperMethodFactory<T extends keyof StateToTypeMap>() {
  // actions
  const updateSingleInput = (field: StateToSingleInputFieldMap<T>, value: string): FormReducerAction => ({
    type: UPDATE_SINGLE_INPUT,
    payload: { field: field as UpdateSingleInputField, value },
  })

  const addInputItem = (field: StateToMultiInputFieldMap<T>, value: string): FormReducerAction => ({
    type: UPDATE_MULTI_INPUT,
    payload: { field: field as UpdateMultiInputField, actionType: 'add', idx: null, value },
  })

  const removeInputItem = (field: StateToMultiInputFieldMap<T>, idx: number): FormReducerAction => ({
    type: UPDATE_MULTI_INPUT,
    payload: { field: field as UpdateMultiInputField, actionType: 'remove', idx, value: '' },
  })

  const addListItem = (field: StateToListFieldMap<T>): FormReducerAction => ({
    type: UPDATE_LIST,
    payload: { field: field as UpdateListField, actionType: 'add', value: '', idx: null },
  })

  const removeListItem = (field: StateToListFieldMap<T>, idx: number): FormReducerAction => ({
    type: UPDATE_LIST,
    payload: { field: field as UpdateListField, actionType: 'remove', value: '', idx },
  })

  const replaceListItem = (field: StateToListFieldMap<T>, idx: number, value: string): FormReducerAction => ({
    type: UPDATE_LIST,
    payload: { field: field as UpdateListField, actionType: 'replace', value, idx },
  })

  const updateSelect = (field: StateToSingleSelectFieldMap<T>, value: string): FormReducerAction => ({
    type: UPDATE_SELECT,
    payload: { field: field as UpdateSelectField, value },
  })

  const updateMultiSelect = (
    field: UpdateMultiSelectField,
    value: string,
    actionType: UpdateActionType,
  ): FormReducerAction => ({
    type: UPDATE_MULTI_SELECT,
    payload: { field, value, actionType },
  })

  const updateAutoComplete = (
    field: UpdateMultiSelectField | UpdateAutoCompleteField,
    value: string[] | AutocompleteDefaultOption[],
  ): FormReducerAction => ({
    type: UPDATE_AUTOCOMPLETE,
    payload: { field, value },
  })

  const setSelect = (field: SelectField, value: string | string[]): FormReducerAction => ({
    type: SET_SELECT,
    payload: { field, value },
  })

  const handleError = (field: keyof StateToTypeMap[T], value: ErrorType): FormReducerAction => ({
    type: HANDLE_ERROR,
    payload: { field: field as string, value },
  })

  const handleWarning = (field: keyof StateToTypeMap[T], value: ErrorType): FormReducerAction => ({
    type: HANDLE_WARNING,
    payload: { field: field as string, value },
  })

  const resetForm = (initialData: StateToTypeMap[T]): FormReducerAction => ({
    type: RESET_FORM,
    payload: initialData,
  })
  return {
    updateSingleInput,
    addInputItem,
    removeInputItem,
    addListItem,
    removeListItem,
    replaceListItem,
    handleError,
    handleWarning,
    updateSelect,
    updateMultiSelect,
    setSelect,
    resetForm,
    updateAutoComplete,
  }
}

export type FormHelperFunctions = ReturnType<typeof formHelperMethodFactory>

export default formReducer
